import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { zodValidator } from "@tanstack/zod-adapter";
import { Loader2Icon, LogOutIcon } from "lucide-react";

import { AuthenticationAuthenticate } from "@/components/Authentication/Login/Authenticate";
import { AuthenticationForget } from "@/components/Authentication/Login/Forget";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { request, setRequestAccessToken } from "@/lib/request";
import { emptyStorageUser } from "@/lib/storage";
import { User } from "@/lib/types";
import { captureEvent, sanitizeErrorReason } from "@/lib/analytics";

const searchSchema = z.object({
  redirect_uri: z.string().optional().catch(undefined),
  returnTo: z.string().optional().catch(undefined),
  state: z.string().optional().catch(undefined),
});

type AuthView = "login" | "forget";
type AuthStatus = "checking" | "authenticated" | "unauthenticated";

type LoginSuccess = {
  access_token: string;
  new_user: boolean;
};

export const Route = createFileRoute("/oauth/login")({
  validateSearch: zodValidator(searchSchema),
  component: RouteComponent,
});

function RouteComponent() {
  const { redirect_uri: redirectUri, returnTo, state } = Route.useSearch();
  const navigate = useNavigate();

  const [status, setStatus] = useState<AuthStatus>("checking");
  const [view, setView] = useState<AuthView>("login");
  const [user, setUser] = useState<User | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isProceedEnabled, setIsProceedEnabled] = useState(false);

  const enableTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const redirectTarget = returnTo ?? redirectUri ?? null;

  const sanitizedRedirect = useMemo(() => {
    if (!redirectTarget) {
      return null;
    }

    try {
      return new URL(redirectTarget, window.location.href).toString();
    } catch {
      return redirectTarget;
    }
  }, [redirectTarget]);

  const clearStoredToken = useCallback(() => {
    localStorage.removeItem("unideck.auth.accessToken");
    setRequestAccessToken(null);
    setUser(null);
  }, []);

  const hydrateUser = useCallback(
    async (token?: string) => {
      const accessToken =
        token ?? localStorage.getItem("unideck.auth.accessToken");

      if (!accessToken) {
        setStatus("unauthenticated");
        setUser(null);
        return;
      }

      setStatus("checking");
      setSessionError(null);
      setRequestAccessToken(accessToken);

      try {
        const res = await request.get<{ data: User }>("/user/info");
        setUser(res.data.data);
        localStorage.setItem("unideck.auth.accessToken", accessToken);
        setStatus("authenticated");
        captureEvent("onboarding_connect_succeeded", {
          service: "oauth_login",
          flow: "callback",
        });
      } catch (err) {
        clearStoredToken();
        setStatus("unauthenticated");
        captureEvent("onboarding_connect_failed", {
          service: "oauth_login",
          flow: "callback",
          reason: sanitizeErrorReason(err),
        });
        setSessionError(
          err instanceof Error
            ? err.message
            : "We could not verify your session. Please sign in again.",
        );
      }
    },
    [clearStoredToken],
  );

  const handleLogin = useCallback(
    async ({ access_token: accessToken }: LoginSuccess) => {
      await hydrateUser(accessToken);
      setView("login");
    },
    [hydrateUser],
  );

  const handleLogout = useCallback(async () => {
    setIsLoggingOut(true);
    try {
      await emptyStorageUser();
    } finally {
      captureEvent("onboarding_connect_failed", {
        service: "oauth_login",
        flow: "oauth",
        reason: "User logged out during oauth flow",
      });
      clearStoredToken();
      setStatus("unauthenticated");
      setSessionError(null);
      setView("login");
      setIsLoggingOut(false);
    }
  }, [clearStoredToken]);

  const handleProceed = useCallback(() => {
    if (!isProceedEnabled || !user) {
      return;
    }

    captureEvent("onboarding_connect_succeeded", {
      service: "oauth_login",
      flow: "oauth",
    });

    if (sanitizedRedirect) {
      window.location.href = state
        ? (() => {
            try {
              const url = new URL(sanitizedRedirect);
              if (!url.searchParams.has("state")) {
                url.searchParams.set("state", state);
              }
              return url.toString();
            } catch {
              return sanitizedRedirect;
            }
          })()
        : sanitizedRedirect;
    } else if (window.opener) {
      window.opener.postMessage({ type: "unideck:oauth:authorized" }, "*");
      window.close();
    } else {
      if (window.history.length > 1) {
        window.history.back();
      } else {
        window.location.href = "/";
      }
    }
  }, [isProceedEnabled, sanitizedRedirect, state, user]);

  const hasAuthCookie = useMemo(() => {
    if (typeof document === "undefined") return false;
    return document.cookie
      .split(";")
      .some(c => c.trim().startsWith("access_token="));
  }, []);

  useEffect(() => {
    if (!hasAuthCookie) {
      setStatus("unauthenticated");
      setUser(null);
      return;
    }
    hydrateUser();
  }, [hydrateUser, hasAuthCookie]);

  useEffect(() => {
    if (!redirectTarget) {
      navigate({
        to: "/",
        search: {
          change_password_token: undefined,
          reset_password_token: undefined,
        },
      });
    }
  }, [navigate, redirectTarget]);

  useEffect(() => {
    if (!redirectTarget) {
      return;
    }

    captureEvent("onboarding_connect_started", {
      service: "oauth_login",
      flow: "oauth",
    });
  }, [redirectTarget]);

  useEffect(() => {
    if (status === "authenticated") {
      setIsProceedEnabled(false);
      if (enableTimerRef.current) {
        clearTimeout(enableTimerRef.current);
      }
      enableTimerRef.current = setTimeout(() => {
        setIsProceedEnabled(true);
        enableTimerRef.current = undefined;
      }, 2500);
    } else {
      if (enableTimerRef.current) {
        clearTimeout(enableTimerRef.current);
        enableTimerRef.current = undefined;
      }
      setIsProceedEnabled(false);
    }

    return () => {
      if (enableTimerRef.current) {
        clearTimeout(enableTimerRef.current);
        enableTimerRef.current = undefined;
      }
    };
  }, [status]);

  if (!redirectTarget) {
    return null;
  }

  return (
    <div className="flex min-h-screen w-screen items-center justify-center bg-background px-4 py-8">
      <Card className="w-full max-w-lg border-border/60 bg-card/90 shadow-2xl backdrop-blur">
        <CardHeader>
          <CardTitle>Authorize External Application</CardTitle>
          <CardDescription>
            {status === "authenticated"
              ? `Signed in as ${user?.displayName ?? user?.email ?? "Unknown user"}.`
              : "Sign in to continue with the external application."}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-6">
          {status === "checking" ? (
            <div className="flex flex-col items-center gap-3 py-6">
              <Loader2Icon className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Checking your session…
              </span>
            </div>
          ) : status === "authenticated" ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-border/60 bg-muted/20 p-4">
                <p className="text-sm text-muted-foreground">Logged in as</p>
                <p className="text-lg font-semibold text-white">
                  {user?.displayName || user?.email}
                </p>
                {user?.email ? (
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                ) : null}
              </div>
              <Alert className="border-border/60 bg-muted/20">
                <AlertTitle>Ready to proceed</AlertTitle>
                <AlertDescription>
                  {sanitizedRedirect
                    ? "We will redirect you to the application's callback once you continue."
                    : "You can close this window or press proceed to let the application continue."}
                </AlertDescription>
              </Alert>
            </div>
          ) : (
            <div className="space-y-4">
              {sessionError ? (
                <Alert variant="destructive" className="border-none">
                  <AlertTitle>Session expired</AlertTitle>
                  <AlertDescription>{sessionError}</AlertDescription>
                </Alert>
              ) : null}
              {view === "login" ? (
                <AuthenticationAuthenticate
                  onLogin={handleLogin}
                  goToForget={() => setView("forget")}
                />
              ) : (
                <AuthenticationForget goToLogin={() => setView("login")} />
              )}
            </div>
          )}
        </CardContent>
        {status === "authenticated" ? (
          <CardFooter className="flex flex-col gap-3">
            <Button
              type="button"
              variant="default"
              className="w-full"
              disabled={!isProceedEnabled}
              onClick={handleProceed}
            >
              {!isProceedEnabled ? (
                <span className="flex items-center gap-2">
                  <Loader2Icon className="h-4 w-4 animate-spin" />
                  Preparing…
                </span>
              ) : (
                "Proceed"
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleLogout}
              disabled={isLoggingOut}
            >
              {isLoggingOut ? (
                <span className="flex items-center gap-2">
                  <Loader2Icon className="h-4 w-4 animate-spin" />
                  Logging out…
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <LogOutIcon className="h-4 w-4" />
                  Log out
                </span>
              )}
            </Button>
          </CardFooter>
        ) : null}
      </Card>
    </div>
  );
}
